---
meta:
  - title: Conceptos básicos de Playbooks - IUSPRO Docs
  - description: Introducción a los playbooks y sus ejecuciones.
---

# Conceptos básicos de Playbooks

La función de Playbooks le permite revisar documentos de forma automática según criterios que usted define. Es especialmente útil para revisar muchos documentos a la vez.

## Dos secciones principales

### 1. Playbooks

Un playbook es una guía que define cómo revisar un tipo específico de documento.

Por ejemplo:
- Playbook para revisar contratos
- Playbook para revisar demandas
- Playbook para revisar cualquier tipo de documento legal

### 2. Ejecuciones

Una ejecución es cada aplicación de un playbook a un documento. Conserva el resultado de comprobar ese documento contra las reglas del playbook.

## ¿Cómo funciona?

### Paso 1: Crear un Playbook

Una persona con experiencia revisando ese tipo de documentos crea el playbook una sola vez.

### Paso 2: Usar el Playbook para revisar

Después, cualquier persona puede:

- Subir desde 1 hasta 1000 documentos
- Seleccionar qué playbook usar
- El sistema revisa todos automáticamente

### Paso 3: Ver resultados

Para cada documento revisado, verá:

- ✅ **Reglas que se cumplieron**
- ⚠️ **Reglas con alternativa aceptable**
- ❌ **Reglas que no se cumplieron**

## ¿Qué son las reglas?

Un playbook está compuesto de reglas. Cada regla verifica algo específico del documento.

Por ejemplo, para una demanda:

- **Regla 1**: Identificación de partes y representantes
- **Regla 2**: Hechos enumerados de manera clara
- **Regla 3**: Fundamentos de derecho
- **Regla 4**: Pretensión principal claramente indicada

Cada regla tiene tres posibles estados:

1. **Posición inicial**: Lo ideal que debe cumplirse
2. **Alternativa**: Algo aceptable si no se cumple lo ideal
3. **No aceptable**: Por defecto, si no se cumple la posición inicial

## Casos de uso

### Revisar muchos contratos

Por ejemplo, necesita revisar 100 contratos. En lugar de revisarlos uno por uno:

1. Crea un playbook con las cláusulas que deben tener
2. Sube los 100 contratos
3. El sistema los revisa todos
4. Puede filtrar cuáles tienen problemas y enfocarse en esos

### Revisar demandas

Puede crear un playbook con los requisitos mínimos de una demanda y revisar múltiples demandas rápidamente.

## Beneficios

- **Ahorra muchísimo tiempo** al revisar documentos
- **Consistencia**: Todos los documentos se revisan con los mismos criterios
- **Eficiencia**: Puede procesar cientos o miles de documentos
- **Enfoque**: Se concentra solo en los documentos con problemas

## Inversión inicial

Requiere una **pequeña inversión inicial de tiempo** de la persona que cree el playbook. Pero una vez creado, ahorra mucho tiempo en todas las ejecuciones futuras.

## Próximos pasos

- Aprenda a [crear un playbook](/guias/revisiones-como-crear-manual)
- Aprenda a [revisar documentos por lote](/guias/revisiones-revisar-documentos-por-lote)
